import { Mapper } from "../../../../../lib/utils/mapper";
import { HttpStatusCode } from "../../../../common/http-status-code";
import { getAxiosPyrgosConfig } from "../../../../common/pyrgos";
import type { IApiClientService } from "../../../../services/ApiClient/ApiClientService.interface";
import AxiosApiClientInstance from "../../../../services/AxiosApiClientInstance/AxiosApiClientInstance";
import type { IConversationMessageDto } from "../../dtos/conversation-message.dto";
import { ConversationMessageDto } from "../../dtos/conversation-message.dto";
import type { IConversationDto } from "../../dtos/conversation.dto";
import { ConversationDto } from "../../dtos/conversation.dto";
import type { ISessionDto } from "../../dtos/session.dto";
import { SessionDto } from "../../dtos/session.dto";
import type { IUpdateMessageFeedbackDto } from "../../dtos/update-message-feedback.dto";
import { UpdateMessageFeedbackDto } from "../../dtos/update-message-feedback.dto";
import type { IUpdateMessageNegativeFeedbackReasonDto } from "../../dtos/update-message-negative-feedback-reason.dto";
import { UpdateMessageNegativeFeedbackReasonDto } from "../../dtos/update-message-negative-feedback-reason.dto";
import type { FeedbackEnum } from "../../enums/feedback.enum";
import type { NegativeFeedbackReasonEnum } from "../../enums/negative-feedback-reason.enum";
import type { IConversationRemoteDataSource } from "./conversation-remote.datasource.interface";

export class ConversationRemoteDataSource implements IConversationRemoteDataSource {
  private readonly _OPTIONS = getAxiosPyrgosConfig();
  private readonly _BASE_URL = "/conversations";

  constructor(private readonly _client: IApiClientService = new AxiosApiClientInstance()) {}

  public async createNewSession(): Promise<ISessionDto> {
    const { data } = await this._client.post<null, SessionDto>(`${this._BASE_URL}/`, null, this._OPTIONS);

    if (!data) {
      throw new Error("Error while creating new session");
    }

    const sessionDto = Mapper.mapData(SessionDto, data);
    return sessionDto;
  }

  public async getLastSession(): Promise<ISessionDto | void> {
    const { data, status } = await this._client.get<SessionDto | void>(
      `${this._BASE_URL}/get-last-session`,
      this._OPTIONS
    );

    const isNoContentResponse = status === HttpStatusCode.NO_CONTENT;
    if (isNoContentResponse) {
      return;
    }

    if (!data) {
      throw new Error("Error while retrieving last session");
    }

    const sessionDto = Mapper.mapData(SessionDto, data);
    return sessionDto;
  }

  public async getAllMessagesForSession(sessionId: string): Promise<IConversationMessageDto[]> {
    const { data } = await this._client.get<IConversationMessageDto[]>(`${this._BASE_URL}/${sessionId}/messages`, {
      ...this._OPTIONS,
      params: {
        order: "ASC",
      },
    });

    if (!data) {
      throw new Error("No session messages available");
    }

    const messagesForSession = Mapper.mapArrayData(ConversationMessageDto, data);
    return messagesForSession;
  }

  public async getAllConversations(): Promise<IConversationDto[]> {
    const { data } = await this._client.get<IConversationDto[]>(`${this._BASE_URL}`, this._OPTIONS);

    if (!data) {
      throw new Error("No conversations available");
    }

    const conversations = Mapper.mapArrayData(ConversationDto, data);
    return conversations;
  }

  public async cloneFromPreviousChat(): Promise<ISessionDto> {
    const { data } = await this._client.post<null, ISessionDto>(
      `${this._BASE_URL}/clone-messages-from-previous-session`,
      null,
      this._OPTIONS
    );

    if (!data) {
      throw new Error("No previous session available");
    }

    const sessionDto = Mapper.mapData(SessionDto, data);
    return sessionDto;
  }

  public async updateMessageFeedback(
    messageId: number,
    feedback: FeedbackEnum | null
  ): Promise<IConversationMessageDto> {
    const payload = new UpdateMessageFeedbackDto({ feedback });
    const { data } = await this._client.patch<IUpdateMessageFeedbackDto, IConversationMessageDto>(
      `${this._BASE_URL}/messages/${messageId}/feedback`,
      payload,
      this._OPTIONS
    );

    if (!data) {
      throw new Error("Error while updating message feedback");
    }

    const messageDto = Mapper.mapData(ConversationMessageDto, data);
    return messageDto;
  }

  public async updateNegativeFeedbackReason(
    messageId: number,
    negativeFeedbackReason: NegativeFeedbackReasonEnum
  ): Promise<IConversationMessageDto> {
    const payload = new UpdateMessageNegativeFeedbackReasonDto({ negativeFeedbackReason });
    const { data } = await this._client.patch<IUpdateMessageNegativeFeedbackReasonDto, IConversationMessageDto>(
      `${this._BASE_URL}/messages/${messageId}/negative-feedback-reason`,
      payload,
      this._OPTIONS
    );

    if (!data) {
      throw new Error("Error while updating message's negative feedback reason");
    }

    const messageDto = Mapper.mapData(ConversationMessageDto, data);
    return messageDto;
  }
}
